import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { PencilSimple, Plus, Trash } from "@phosphor-icons/react";
import { loadMessageTemplates } from "@/components/MessageTemplatePicker";

const EMPTY = { name: "", subject: "", body: "" };

function TemplateForm({ initial, onSave, onCancel }) {
  const [v, setV] = useState(initial);
  return (
    <div className="panel p-4 space-y-2" data-testid="msg-tpl-form">
      <input value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} placeholder="Template name"
        data-testid="msg-tpl-form-name" className="w-full px-3 py-2" />
      <input value={v.subject} onChange={(e) => setV({ ...v, subject: e.target.value })} placeholder="Subject"
        data-testid="msg-tpl-form-subject" className="w-full px-3 py-2" />
      <textarea value={v.body} onChange={(e) => setV({ ...v, body: e.target.value })} rows={6}
        placeholder="Message body. Use {name}, {first_name}, {email}"
        data-testid="msg-tpl-form-body" className="w-full px-3 py-2" />
      <div className="flex gap-2">
        <button onClick={() => onSave(v)} data-testid="msg-tpl-form-save" className="btn-primary text-xs">Save</button>
        <button onClick={onCancel} className="btn-ghost text-xs">Cancel</button>
      </div>
    </div>
  );
}

export default function MessageTemplatesPanel() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [editing, setEditing] = useState(null);
  const refresh = () => loadMessageTemplates(true).then(setItems).catch(() => {}).finally(() => setLoaded(true));
  useEffect(() => { refresh(); }, []);

  const save = async (v) => {
    try {
      if (editing === "new") await api.post("/admin/message-templates", v);
      else await api.put(`/admin/message-templates/${editing}`, v);
      toast.success("Template saved");
      setEditing(null); refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed"); }
  };
  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    await api.delete(`/admin/message-templates/${t.template_id}`);
    toast.success("Template deleted"); refresh();
  };

  return (
    <div className="space-y-3 max-w-3xl" data-testid="message-templates-panel">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm" style={{ color: "var(--text-dim)" }}>
          Reusable messages for the client composer and thread replies. Placeholders: {"{name}"}, {"{first_name}"}, {"{email}"}.
        </p>
        {editing === null && (
          <button onClick={() => setEditing("new")} data-testid="msg-tpl-new" className="btn-primary text-xs shrink-0">
            <Plus size={12} weight="bold" /> New template
          </button>
        )}
      </div>
      {editing === "new" && <TemplateForm initial={EMPTY} onSave={save} onCancel={() => setEditing(null)} />}
      {loaded && items.length === 0 && editing === null && <div className="panel p-6 text-center label">No message templates yet</div>}
      {items.map(t => editing === t.template_id ? (
        <TemplateForm key={t.template_id} initial={t} onSave={save} onCancel={() => setEditing(null)} />
      ) : (
        <div key={t.template_id} className="panel p-4" data-testid={`msg-tpl-row-${t.template_id}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-semibold">{t.name}</p>
              {t.subject && <p className="text-sm mt-0.5">{t.subject}</p>}
              <p className="text-xs mt-1 whitespace-pre-line line-clamp-3" style={{ color: "var(--text-dim)" }}>{t.body}</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => setEditing(t.template_id)} data-testid={`msg-tpl-edit-${t.template_id}`}
                className="btn-ghost text-xs" aria-label="Edit"><PencilSimple size={12} weight="bold" /></button>
              <button onClick={() => remove(t)} data-testid={`msg-tpl-delete-${t.template_id}`}
                className="btn-ghost text-xs" aria-label="Delete"><Trash size={12} weight="bold" /></button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
